import { useStudioStore } from '../../stores/studioStore';
import { clsx, sessionKey } from '../../lib/utils';

export function SessionGrid() {
  const tracks = useStudioStore((s) => s.tracks);
  const session = useStudioStore((s) => s.session);
  const selectedTrackId = useStudioStore((s) => s.selectedTrackId);
  const launchScene = useStudioStore((s) => s.launchScene);
  const launchTrack = useStudioStore((s) => s.launchTrack);
  const queueClip = useStudioStore((s) => s.queueClip);
  const stopTrack = useStudioStore((s) => s.stopTrack);
  const stopAllSessionClips = useStudioStore((s) => s.stopAllSessionClips);
  const selectTrack = useStudioStore((s) => s.selectTrack);

  const cols = Math.max(1, tracks.length);

  return (
    <div className="min-h-0 flex-1 overflow-auto p-3">
      <div
        className="grid gap-1.5"
        style={{
          gridTemplateColumns: `120px repeat(${cols}, minmax(140px, 1fr))`,
        }}
      >
        <div className="flex flex-col items-stretch justify-center gap-1 rounded border border-daw-border bg-daw-surface2 p-2 text-[10px] uppercase tracking-wide text-daw-muted">
          <span>Scenes</span>
          <button
            type="button"
            className="rounded border border-red-900/60 bg-red-950/40 px-2 py-1 text-[10px] text-red-300"
            onClick={stopAllSessionClips}
          >
            ■ All
          </button>
          {session.capture && (
            <span className="rounded bg-red-600 px-1 py-0.5 text-center text-[9px] font-bold text-white">
              REC→ARR
            </span>
          )}
        </div>

        {tracks.map((track) => (
          <div key={track.id} className="grid grid-cols-[1fr_28px] gap-1">
            <button
              type="button"
              className={clsx(
                'rounded border px-2 py-2 text-left text-[11px]',
                selectedTrackId === track.id
                  ? 'border-daw-accent2 bg-daw-accent2/10'
                  : 'border-daw-border bg-daw-surface2',
              )}
              onClick={() => {
                selectTrack(track.id);
                launchTrack(track.id);
              }}
              title="Launch first clip"
            >
              <span
                className="mr-1 inline-block h-2 w-2 rounded-sm"
                style={{ background: track.color }}
              />
              {track.name}
            </button>
            <button
              type="button"
              className="rounded border border-red-900/50 bg-red-950/30 text-xs text-red-300"
              title="Stop track"
              onClick={() => stopTrack(track.id)}
            >
              ■
            </button>
          </div>
        ))}

        {Array.from({ length: session.sceneCount }, (_, scene) => (
          <SceneRow key={scene} scene={scene} />
        ))}
      </div>
    </div>
  );
}

function SceneRow({ scene }: { scene: number }) {
  const tracks = useStudioStore((s) => s.tracks);
  const session = useStudioStore((s) => s.session);
  const launchScene = useStudioStore((s) => s.launchScene);
  const queueClip = useStudioStore((s) => s.queueClip);
  const selectTrack = useStudioStore((s) => s.selectTrack);
  const stopTrack = useStudioStore((s) => s.stopTrack);

  return (
    <>
      <div className="grid grid-cols-[1fr_28px] gap-1">
        <button
          type="button"
          className="rounded border border-daw-border bg-daw-surface3 px-2 py-2 text-left text-[11px]"
          onClick={() => launchScene(scene)}
        >
          ▶ Scene {scene + 1}
        </button>
        <button
          type="button"
          className="rounded border border-red-900/50 bg-red-950/30 text-xs text-red-300"
          title="Stop scene clips"
          onClick={() => {
            tracks.forEach((t) => {
              if (session.playingByTrack[t.id] === sessionKey(t.id, scene)) {
                stopTrack(t.id);
              }
            });
          }}
        >
          ■
        </button>
      </div>

      {tracks.map((track) => {
        const key = sessionKey(track.id, scene);
        const slot = session.slots[key];
        const queued = session.queuedKeys.includes(key);
        const playing = session.playingByTrack[track.id] === key;
        return (
          <button
            key={key}
            type="button"
            className={clsx(
              'min-h-[52px] rounded border px-2 py-1.5 text-left',
              !slot && 'border-daw-border bg-daw-surface2/60 opacity-70',
              slot && 'border-blue-500/50 bg-daw-surface2',
              queued && 'border-amber-500 bg-amber-500/15',
              playing && 'border-emerald-500 bg-emerald-500/15',
            )}
            onClick={() => {
              selectTrack(track.id);
              if (slot) queueClip(track.id, scene);
            }}
          >
            {slot ? (
              <>
                <div className="truncate text-[11px] font-semibold">{slot.label}</div>
                <div className="mt-0.5 text-[9px] text-daw-muted">
                  {(slot.launchMode || session.defaultLaunchMode)[0].toUpperCase()}
                  {slot.followAction && slot.followAction !== 'none'
                    ? ` · F:${slot.followAction}`
                    : ''}
                </div>
              </>
            ) : (
              <span className="text-[11px] text-daw-muted">+ empty</span>
            )}
          </button>
        );
      })}
    </>
  );
}
